import axios from 'axios';

// Create an instance of axios with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the auth token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common errors
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Compte désactivé côté serveur : la session ne peut plus rien faire,
    // on la ferme au lieu de laisser l'utilisateur enchaîner les 403.
    if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_DISABLED') {
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post('/api/auth/refresh', {}, { withCredentials: true });
        const newToken = data.token;
        localStorage.setItem('token', newToken);
        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// API functions for supply sources
api.supplySource = {
  // Get all supply sources (internal sites and distributors)
  getAll: () => api.get('/api/supply-sources'),
  
  // Initialize default internal sites (admin only)
  initialize: () => api.post('/api/supply-sources/initialize'),
};

// API functions for distributors
api.distributors = {
  // Get all distributors
  getAll: (showInactive = false, search = '', page = 1, limit = 10, zoneId = '') =>
    api.get(`/api/distributors?showInactive=${showInactive}&search=${encodeURIComponent(search)}&page=${page}&limit=${limit}${zoneId ? `&zoneId=${encodeURIComponent(zoneId)}` : ''}`),
  
  // Get a single distributor
  getById: (id) => api.get(`/api/distributors/${id}`),
  
  // Create a new distributor
  create: (data) => api.post('/api/distributors', data),
  
  // Update a distributor
  update: (id, data) => api.put(`/api/distributors/${id}`, data),
  
  // Deactivate a distributor
  deactivate: (id) => api.delete(`/api/distributors/${id}`),
  
  // Import distributors from CSV
  import: (formData) => {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    return api.post('/api/distributors/import', formData, config);
  },
};

// API functions for internal sites
api.internalSites = {
  // Get all internal sites
  getAll: (showInactive = false) => 
    api.get(`/api/internal-sites?showInactive=${showInactive}`),
  
  // Get a single internal site
  getById: (id) => api.get(`/api/internal-sites/${id}`),
  
  // Create a new internal site
  create: (data) => api.post('/api/internal-sites', data),
  
  // Update an internal site
  update: (id, data) => api.put(`/api/internal-sites/${id}`, data),
  
  // Deactivate an internal site
  deactivate: (id) => api.delete(`/api/internal-sites/${id}`),
  
  // Initialize default internal sites
  initialize: () => api.post('/api/internal-sites/initialize'),
};

// API functions for marketing validation
api.marketingValidation = {
  validate: (id, data) => api.put(`/api/requests/${id}/validate-marketing`, data),
  getRequestSupplySource: (id) => api.get(`/api/requests/${id}/supply-source`),
  review: (id, data) => api.put(`/api/marketing-validation/${id}/review`, data),
  approbation: (id, data) => api.put(`/api/marketing-validation/${id}/approbation`, data),
};

// API functions for force de vente validation
api.forceDeVente = {
  // Assign a request to a force de vente user
  assign: (requestId, data) => api.post(`/api/force-de-vente/assign/${requestId}`, data),

  // Get electronic signature status for a request
  getSignatureStatus: (requestId) => api.get(`/api/force-de-vente/signature-status/${requestId}`),

  // Validate a request (étape 1 - génération de la décharge)
  validate: (requestId, data) => api.post(`/api/force-de-vente/validate/${requestId}`, data),

  // Upload discharge scan (étape 2 - téléversement du scan de la décharge signée)
  uploadScan: (id, formData) => {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    return api.post(`/api/force-de-vente/upload-scan/${id}`, formData, config);
  },

  // Reject a request
  reject: (requestId, data) => api.post(`/api/force-de-vente/reject/${requestId}`, data),

  // Resend signature email to a signer
  resendEmail: (requestId, signerType) => api.post(`/api/force-de-vente/resend-email/${requestId}`, { signerType }),

  // Cancel discharge and revert to pending state
  cancelDischarge: (requestId) => api.put(`/api/force-de-vente/cancel-discharge/${requestId}`),

  // Get discharge recap (summary of discharges)
  getDischargeRecap: (params) => api.get('/api/force-de-vente/discharge-recap', { params }),

  // Upload the attribution signed by hand by the beneficiary (PDF/JPG/PNG)
  uploadSignedAttribution: (requestId, formData) =>
    api.post(`/api/force-de-vente/upload-signed-attribution/${requestId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Étape distributeur : le distributeur signe la décharge depuis son compte
api.distributeur = {
  getMine: () => api.get('/api/distributeur/mine'),
  validate: (id, data) => api.post(`/api/distributeur/validate/${id}`, data),
  reject: (id, data) => api.post(`/api/distributeur/reject/${id}`, data),
};

// Étape chef de zone : visa de zone, aucun document généré
api.chefDeZone = {
  validate: (id, data) => api.post(`/api/chef-de-zone/validate/${id}`, data),
  reject: (id, data) => api.post(`/api/chef-de-zone/reject/${id}`, data),
};

// API functions for QR sign sessions
api.sign = {
  // Regenerate a QR sign token for a specific signer type.
  // Works even when the previous session expired (TTL-deleted by MongoDB).
  regenerateQR: (requestId, signerType) =>
    api.post(`/api/sign/session/${requestId}/regenerate`, { signerType }),
};

// API functions for directrice marketing validation
api.directrice = {
  // Validate a request
  validate: (id, data) => api.post(`/api/directrice/validate/${id}`, data),
  
  // Reject a request
  reject: (id, data) => api.post(`/api/directrice/reject/${id}`, data),
};

// API functions for commercial validation
api.commercial = {
  // Validate a request
  validate: (id, data) => api.post(`/api/commercial/validate/${id}`, data),
  
  // Reject a request
  reject: (id, data) => api.post(`/api/commercial/reject/${id}`, data),
};

// API functions for documents
api.documents = {
  // Get all documents
  getAll: () => api.get('/api/documents'),
  
  // Get a single document
  getById: (id) => api.get(`/api/documents/${id}`),
  
  // Download a document
  download: (id) => api.get(`/api/documents/${id}/download`, { responseType: 'blob' }),
  
  // Get documents by request
  getByRequest: (requestId) => api.get(`/api/documents/request/${requestId}`),
};

// API functions for users
api.users = {
  // Get all users (admin only)
  getAll: () => api.get('/api/users'),
  
  // Get a single user
  getById: (id) => api.get(`/api/users/${id}`),
  
  // Update a user
  update: (id, data) => api.put(`/api/users/${id}`, data),
  
  // Activer / désactiver un utilisateur (admin only)
  setStatus: (id, isActive) => api.put(`/api/users/${id}/status`, { isActive }),

  // Réinitialiser le mot de passe d'un utilisateur (admin only)
  resetPassword: (id, password) => api.put(`/api/users/${id}/password`, { password }),

  // Delete a user (admin only)
  delete: (id) => api.delete(`/api/users/${id}`),
  
  // Upload user signature
  uploadSignature: (id, formData) => {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    return api.put(`/api/users/${id}/signature`, formData, config);
  },
  
  // Upload user stamp
  uploadStamp: (id, formData) => {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    return api.put(`/api/users/${id}/stamp`, formData, config);
  },
};

// API functions for statistics
// `query` : { from, to, dateBasis, filters } ; `spec` : { rows, cols, measures, sort }.
const statsParams = ({ from, to, dateBasis, filters } = {}, spec = {}) => ({
  ...(from && { from: from.toISOString() }),
  ...(to && { to: to.toISOString() }),
  dateBasis,
  filters: JSON.stringify(filters || {}),
  ...Object.fromEntries(Object.entries(spec).map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v])),
});

api.statistics = {
  options: (query) => api.get('/api/statistics/analytics/options', { params: statsParams(query) }),
  overview: (query) => api.get('/api/statistics/analytics/overview', { params: statsParams(query) }),
  pivot: (query, spec) => api.get('/api/statistics/analytics/pivot', { params: statsParams(query, spec) }),
  exportPivot: (query, spec, format = 'xlsx') =>
    api.get('/api/statistics/analytics/pivot/export', { params: { ...statsParams(query, spec), format }, responseType: 'blob' }),
  // Vues enregistrées (favoris), privées à l'utilisateur.
  views: {
    list: () => api.get('/api/statistics/analytics/views'),
    create: (name, config) => api.post('/api/statistics/analytics/views', { name, config }),
    remove: (id) => api.delete(`/api/statistics/analytics/views/${id}`),
  },
};

api.requests = {
  exportProducts: (requestId) =>
    api.get(`/api/requests/${requestId}/export-products`, { responseType: 'blob' }),
  getProducts: (requestId) =>
    api.get(`/api/requests/${requestId}/products`),
};

api.zones = {
  getAll: () => api.get('/api/zones'),
  getMine: () => api.get('/api/zones/mine'),
  getById: (zoneId) => api.get(`/api/zones/${zoneId}`),
};

export default api;
